import { ASSIGNED_BOOKING_TYPE, CREATE_BOOKING_TYPE, DEFAULT_RESOURCE_NAMES, MODALS } from "../../../config/params";
import { Box, Divider, Typography } from "@mui/material";
import {
  CalendarXIcon,
  CallHospitalIcon,
  ClinicLocationIcon,
  ClockIcon,
  NewCalendarIcon,
  NewStethoScope,
  UserMailIcon,
} from "../../../components/Icons";
import { capitalizeFirstLetterGeneric, formatDoctorNames } from "../../../utils/genericUtils";
import {
  fromUnixToFullDateNum,
  fromUnixToFullVisitdateStr,
  getDateEpochInTimezone,
  getHourFromUnix,
} from "../../../utils/unix-timestamp";
import { useBookingAppointmentTypeStore, useRescheduleAllow } from "../../../hooks";

import BookingReviewDetails from "../../../components/BookingReviewDetails/BookingReviewDetails";
import CloseIcon from "@mui/icons-material/Close";
import GvSecondaryButton from "../../../components/GvSecondaryButton/GvSecondaryButton";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { NewSecondaryButton } from "../../../styles/styledComponents";
import { PetAvatars } from "../../../components";
import { PromoCodeUsed } from "../../../components/PromoCode/PromoCode";
import React from "react";
import { ReasonForVisitContainerStyled } from "../BookingReviewContainer/BookingReviewStyle";
import { UrgentVisitStep } from "../../../state/urgent-visit";
import { getApptTypeLabel } from "../../../components/Appointments/AppointmentCard";
import { getBookingMonth } from "../../../utils/bookingFlow";
import { getContactString } from "../ChangeHospitalContainer/ChangeHospitalContainer";
import { useAppointmentStore } from "../../../hooks/redux-hooks/appointment-store/appointment.store";
import { useModal } from "../../../context/ModalContext";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUrgentVisitStore } from "../../../hooks/redux-hooks/urgent-visit-store/urgent-visit.store";
import { userActivityLogger } from "../../../utils/mixpanel";

export function formatReasonString(input: string): string[] {
  const categoryPairs = input.split(/,(?=[a-zA-Z]+:)/);

  return categoryPairs;
}

export function parseReasonsForVisit(input: string): { reasons: string[]; notes: string } {
  if (input.startsWith("Description:")) {
    const withoutPrefix = input.replace(/^Description:\s*/, "");
    const markerRegex = /,\s*reasons:\s*/gi;
    let lastMarkerIndex = -1;
    let lastMarkerLength = 0;
    let match: RegExpExecArray | null;
    while ((match = markerRegex.exec(withoutPrefix)) !== null) {
      lastMarkerIndex = match.index;
      lastMarkerLength = match[0].length;
    }
    if (lastMarkerIndex >= 0) {
      const notes = withoutPrefix.slice(0, lastMarkerIndex).trim();
      const reasonsStr = withoutPrefix.slice(lastMarkerIndex + lastMarkerLength);
      const reasons = reasonsStr.split(",").map((r) => r.trim()).filter(Boolean);
      return { notes, reasons };
    }
  }
  return {
    notes: "",
    reasons: input.split(",").map((r) => r.trim()).filter(Boolean),
  };
}


export function extractReasonsAndNotes(input: string): { reasons: string[]; notes: string } {
  const source = input ?? "";
  const REASONS_MARKER = "reasons:";
  const DESCRIPTION_MARKER = "Description:";

  let reasons: string[] = [];
  if (source.includes(REASONS_MARKER)) {
    const afterReasons = source.split(REASONS_MARKER)[1] ?? "";
    reasons = afterReasons.split(",").map((r) => r.trim()).filter(Boolean);
  } else if (!source.includes(DESCRIPTION_MARKER)) {
    reasons = source.split(",").map((r) => r.trim()).filter(Boolean);
  }

  let notes = "";
  if (source.includes(DESCRIPTION_MARKER)) {
    let section = source.split(DESCRIPTION_MARKER)[1] ?? "";
    if (source.includes(REASONS_MARKER)) {
      section = section.split(REASONS_MARKER)[0] ?? "";
    }
    notes = section.trim().replace(/,\s*$/, "");
  }

  return { reasons, notes };
}

export const AppointmentReviewContainer = () => {
  const {
    appointmentDetails, groupedAppointments, setGroupedAppointments, setAppointmentDetails,
  } = useAppointmentStore();
  const { closeModal, openModal } = useModal();
  const {
    setDefaultBookingType, setBookingAppointmentType, setAppointmentTypeIndicator,
  } = useBookingAppointmentTypeStore();
  const { petList } = usePetStore();
  const {
    resetUrgentVisit,
    setSelectedPets: setUrgentSelectedPets,
    setStep: setUrgentStep,
    setRescheduleAppointmentId,
    setPetReasons,
    setPetNotes,
    setPetAppointmentMap,
    setSelectedDate,
  } = useUrgentVisitStore();

  const isGrouped = groupedAppointments && groupedAppointments.length > 1;

  const { rescheduleAllowed: groupedRescheduleAllowed } = useRescheduleAllow(
    groupedAppointments?.[0]?.start_at, groupedAppointments?.[0]?.location.timeZone,
  );
  const { rescheduleAllowed: singleRescheduleAllowed } = useRescheduleAllow(
    appointmentDetails?.start_at, appointmentDetails?.location?.timeZone,
  );

  const handleReScheduleVisit = () => {
    userActivityLogger(MIXPANEL_EVENTS.RESCHEDULE_VISIT, {
      referred_from: "booking_details",
      pet_name: appointmentDetails?.pet.name,
      appt_type: getApptTypeLabel(
        appointmentDetails?.appointmentCreateType!),
    });

    if (appointmentDetails?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING) {
      setBookingAppointmentType(CREATE_BOOKING_TYPE.URGENT_BOOKING);
      setAppointmentTypeIndicator(appointmentDetails.appointmentCreateType);
      resetUrgentVisit();
      setSelectedDate(getDateEpochInTimezone(
        appointmentDetails.start_at, appointmentDetails.location.timeZone,
      ));
      setRescheduleAppointmentId(appointmentDetails.id);
      const petToSelect = (petList || []).find((p) => p.id === appointmentDetails.pet.id);
      if (petToSelect) {
        setUrgentSelectedPets([petToSelect]);
        setUrgentStep(UrgentVisitStep.TIME_SELECTION);
        if (appointmentDetails.reasonsForVisit) {
          const parsed = extractReasonsAndNotes(appointmentDetails.reasonsForVisit);
          setPetReasons(petToSelect.id, parsed.reasons);
          setPetNotes(petToSelect.id, parsed.notes);
        }
      }
      openModal(MODALS.URGENT_VISIT_MODAL);
    } else if (appointmentDetails?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING) {
      openModal(MODALS.RESCHEDULE_EXPRESS_VISIT_MODAL);
    } else {
      openModal(MODALS.RESCHEDULE_A_VISIT);
    }
  };

  const handleCancelVisit = () => {
    setAppointmentTypeIndicator(appointmentDetails?.appointmentCreateType as number);
    openModal(MODALS.VISIT_CANCEL);
  };

  const handleCloseIcon = () =>{
    setDefaultBookingType();
    setGroupedAppointments(null);
    closeModal();
  };

  const handleGroupedReschedule = () => {
    const isUrgent = groupedAppointments?.some(
      (appt) => appt.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
    );
    if (isUrgent) {
      setBookingAppointmentType(CREATE_BOOKING_TYPE.URGENT_BOOKING);
      setAppointmentTypeIndicator(ASSIGNED_BOOKING_TYPE.URGENT_BOOKING);
      resetUrgentVisit();
      if (groupedAppointments?.[0]) {
        setSelectedDate(getDateEpochInTimezone(
          groupedAppointments[0].start_at,
          groupedAppointments[0].location.timeZone,
        ));
        setRescheduleAppointmentId(groupedAppointments[0].id);
        setAppointmentDetails(groupedAppointments[0]);
        const petApptMap: Record<string, string> = {};
        const groupType = groupedAppointments[0].appointmentCreateType;
        groupedAppointments
          .filter((appt) => appt.appointmentCreateType === groupType)
          .forEach((appt) => {
            petApptMap[appt.pet.id] = appt.id;
          });
        setPetAppointmentMap(petApptMap);
        const petsToSelect = (petList || []).filter((p) =>
          Object.keys(petApptMap).includes(p.id));
        if (petsToSelect.length > 0) {
          setUrgentSelectedPets(petsToSelect);
          setUrgentStep(UrgentVisitStep.PET_SELECTION);
          groupedAppointments
            .filter((appt) => appt.appointmentCreateType === groupType)
            .forEach((appt) => {
              if (appt.reasonsForVisit) {
                const parsed = extractReasonsAndNotes(appt.reasonsForVisit);
                setPetReasons(appt.pet.id, parsed.reasons);
                setPetNotes(appt.pet.id, parsed.notes);
              }
            });
        }
      }
      openModal(MODALS.URGENT_VISIT_MODAL);
    } else {
      openModal(MODALS.GROUPED_APPOINTMENT_ACTION, undefined, { action: "reschedule" });
    }
  };

  const handleGroupedCancel = () => {
    openModal(MODALS.GROUPED_APPOINTMENT_ACTION, undefined, { action: "cancel" });
  };

  const getAppointmentTypeText = (typeIndicator: number) =>{
    switch (typeIndicator) {
    case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
      return "Drop-Off ";
    case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
      return "Express Visit ";
    default:
      return "Doctor Visit ";
    }
  };

  const handleOpenAddToCalendarModal = () => {
    const timeslot = (appointmentDetails &&
      getHourFromUnix(appointmentDetails.start_at,
        appointmentDetails.location.timeZone));

    const calendarPets = isGrouped ?
      groupedAppointments.map((appt) => appt.pet) :
      (appointmentDetails?.pet ? [appointmentDetails.pet] : []);
    const petNames = calendarPets.map((p) => p.name).join(",");
    const noOfPets = calendarPets.length;
    const eventDate = appointmentDetails &&
      fromUnixToFullVisitdateStr(appointmentDetails?.start_at,
        appointmentDetails?.location?.timeZone);

    const createType = isGrouped ?
      groupedAppointments[0]?.appointmentCreateType :
      appointmentDetails?.appointmentCreateType;
    const isDropOff = createType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING;

    userActivityLogger(
      isDropOff ?
        MIXPANEL_EVENTS.DROP_OFF_ADD_CALENDAR :
        MIXPANEL_EVENTS.ADD_CALENDAR,
      {
        appointment_category: isDropOff ? "drop_off" : "dvm",
        pet_name: petNames,
        no_of_pets: noOfPets,
        date: eventDate,
        time_slot: timeslot,
      },
    );
    openModal(MODALS.ADD_TO_CALENDAR);
  };
  const appointmentTime = fromUnixToFullDateNum(appointmentDetails?.start_at!,
     appointmentDetails?.location.timeZone!);

  const appointmentType = appointmentDetails?.appointmentType.split("-")[0];


  if (isGrouped) {
    const firstAppt = groupedAppointments[0];
    const groupedTime = fromUnixToFullDateNum(firstAppt.start_at, firstAppt.location.timeZone);
    const petDetails = groupedAppointments.map((appt) => ({
      name: appt.pet.name,
      image: appt.pet.image,
    }));
    const isGroupedUrgent = groupedAppointments.some(
      (appt) => appt.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING,
    );
    const allowedToEdit = groupedAppointments.some((appt) => appt.allowedToEdit) ||
      (isGroupedUrgent && groupedRescheduleAllowed);

    return (
      <Box className="h-full flex flex-col gap-6">
        <Box className="flex flex-col gap-2 grow-0 shrink">
          <Box className="flex w-full justify-end cursor-pointer pr-3 pt-1" onClick={handleCloseIcon}>
            <CloseIcon /></Box>
          <Box className="flex w-full flex-col gap-3 items-center justify-center">
            <PetAvatars petDetails={petDetails} maxVisible={3} />
            <div className="font-semibold text-2xl text-center">
              {groupedAppointments.map((appt) =>
                capitalizeFirstLetterGeneric(appt.pet.name)).join(", ")}&apos;s Appointments
            </div>
          </Box>
        </Box>
        <Box className="flex flex-col gap-2 grow overflow-y-auto pr-1">
          {groupedAppointments.map((appt, index) => {
            const apptTime = fromUnixToFullDateNum(appt.start_at, appt.location.timeZone);
            const apptType = appt.appointmentType.split("-")[0];
            return (
              <ReasonForVisitContainerStyled key={appt.id}>
                <Box className="childContainer w-full p-6">
                  <Box className="flex flex-col gap-4 w-full">
                    <Box className="flex gap-3 items-center">
                      <PetAvatars petDetails={[{ name: appt.pet.name, image: appt.pet.image }]}
                        width={40} height={40} />
                      <Box>
                        <Typography variant="h4" className="font-semibold">
                          {capitalizeFirstLetterGeneric(appt.pet.name)}&apos;s{" "}
                          <span className="capitalize">{apptType}</span>
                        </Typography>
                        <Typography variant="body2" className="text-gray-1">
                          {getAppointmentTypeText(appt.appointmentCreateType)} at{" "}
                          <span className="font-semibold">{apptTime.time}</span>
                        </Typography>
                      </Box>
                    </Box>
                    {appt.appointmentCreateType !== ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING &&
                      appt.resources?.length > 0 && appt.resources[0].name &&
                      (formatDoctorNames(appt.resources) !== "") &&
                    <Box className="flex gap-4 font-semibold capitalize">
                      <NewStethoScope width={25} height={25} color={"#262626"} />
                      <Box>{formatDoctorNames(appt.resources)}</Box>
                    </Box>}
                    {appt.reasonsForVisit && (
                      <Typography variant="body2" className="text-gray-1 capitalize">
                        {appt.reasonsForVisit}
                      </Typography>
                    )}
                  </Box>
                  {appt.promoCode && appt.promoCode.length > 0 && (
                    <Box className="flex w-full flex-col gap-3 mt-2">
                      <Typography variant="body1" className="font-semibold text-gray-1">Promo Applied</Typography>
                      {appt.promoCode.map((promo) => (
                        <PromoCodeUsed key={promo} promoCodeUsed={promo}/>
                      ))}
                    </Box>
                  )}
                </Box>
              </ReasonForVisitContainerStyled>
            );
          })}

          <ReasonForVisitContainerStyled>
            <Box className="childContainer w-full p-6">
              <Box className="flex gap-4 w-full">
                <img src="/assets/icons/newCalenderIcon.svg" alt="calendar icon" />
                <Box className="flex gap-[1px] items-start h-full w-fit border-b border-solid
                  border-gv-yellow pb-1">
                  <Typography variant="h4" className="font-semibold">{groupedTime.weekday}</Typography>
                  <Divider orientation="vertical"
                    className="!self-center !mx-[0.2rem] !border-[1px] !border-gray-3 !h-3"/>
                  <Typography variant="h4">
                    {getBookingMonth(
                      firstAppt.start_at.toString(),
                      firstAppt.location.timeZone,
                    )} {groupedTime.day}
                  </Typography>
                </Box>
              </Box>

              <Box className="flex gap-4 w-full">
                <ClinicLocationIcon width={24} height={24} color={"#262626"}/>
                <Box className="flex flex-col gap-1">
                  <Typography variant="h4"><span className="font-semibold">GoodVets {" "}
                    {firstAppt.location.locationName}</span>
                  </Typography>
                  <Typography variant="subtitle1">{firstAppt.location.address}</Typography>
                </Box>
              </Box>

              <Box className="border-b-2 w-[90%] ml-[5%] border-gray-3"></Box>

              <Box className="flex w-full flex-col gap-3">
                <Typography variant="body1" className="font-semibold text-gray-1">Hospital Contact</Typography>
                <a href={`mailto:${firstAppt.location.email}`}>
                  <Box className="flex gap-4 items-center">
                    <UserMailIcon width={24} height={24}/>
                    <Typography className="!cursor-pointer font-semibold" variant="body1">
                      {firstAppt.location.email}
                    </Typography>
                  </Box>
                </a>
                <a className="font-semibold" href={getContactString(firstAppt.location.phoneNumber)}>
                  <Box className="flex gap-4 items-center">
                    <CallHospitalIcon width={24} height={24}/>
                    <Typography variant="body1">
                      {firstAppt.location.phoneNumber}
                    </Typography>
                  </Box>
                </a>
              </Box>
            </Box>
          </ReasonForVisitContainerStyled>
        </Box>
        <Box className="flex flex-col items-center !sticky bottom-2 gap-1">
          {allowedToEdit &&
          <NewSecondaryButton
            startIcon={<NewCalendarIcon />}
            className="text-light-black !mb-0.5"
            onClick={handleGroupedReschedule}>
            Reschedule
          </NewSecondaryButton>}
          {allowedToEdit &&
          <NewSecondaryButton
            className="text-light-black !mb-0.5"
            startIcon={<CalendarXIcon />}
            onClick={handleGroupedCancel}>
            Cancel
          </NewSecondaryButton>}
          <GvSecondaryButton
            type="submit"
            className="!italic !font-semibold hover:!underline"
            onClick={handleOpenAddToCalendarModal}>
            Add to Calendar
          </GvSecondaryButton>
          {!allowedToEdit &&
          <div className="italic font-semibold text-sm text-center my-2 w-80">
            Please contact your hospital to reschedule or cancel these appointments
          </div>}
        </Box>
      </Box>
    );
  }

  return (
    <>
      {appointmentDetails && <Box className="h-full flex flex-col gap-6">
        <Box className="flex flex-col gap-2 grow-0 shrink">
          <Box className="flex w-full justify-end cursor-pointer pr-3 pt-1" onClick={handleCloseIcon}>
            <CloseIcon /></Box>
          <Box className="flex w-full flex-col gap-3 items-center justify-center">
            <PetAvatars
              petDetails={[appointmentDetails?.pet]} />
            <div className="font-semibold text-2xl text-center">
              {capitalizeFirstLetterGeneric(appointmentDetails?.pet.name)}&apos;s {appointmentType}
            </div>
          </Box>
        </Box>
        <Box className="flex flex-col gap-2 grow overflow-y-auto  pr-1">
          <ReasonForVisitContainerStyled>
            <Box className="childContainer w-full p-6">
              <Box className="flex flex-col gap-4 w-full">
                <BookingReviewDetails modal="AppointmentReview"
                  isExpressVisit={appointmentDetails.appointmentCreateType === ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING} />
                {appointmentDetails.appointmentCreateType !== ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING &&
                  appointmentDetails && appointmentDetails?.resources?.length &&
                  appointmentDetails.resources[0].name &&
                  (formatDoctorNames(appointmentDetails.resources) !== "") &&
              <Box className="flex gap-4 font-semibold capitalize">
                <NewStethoScope width={25} height={25} color={"#262626"} />
                <Box>{formatDoctorNames(appointmentDetails.resources)}</Box>
              </Box>}
                {appointmentDetails.appointmentCreateType === ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING &&
              <Box className="flex gap-4 font-semibold capitalize">
                <NewStethoScope width={25} height={25} color={"#262626"} />
                <Box>{DEFAULT_RESOURCE_NAMES.EXPRESS_VISIT}</Box>
              </Box>
                }
              </Box>
              <Box className="border-b-2 w-[90%] ml-[5%] border-gray-3"></Box>
              <Box className="flex gap-4 w-full">
                <img src="/assets/icons/newCalenderIcon.svg" alt="calendar icon" />
                <Box className="flex gap-[1px] items-start h-full w-fit border-b border-solid
                  border-gv-yellow pb-1">
                  <Typography variant="h4" className="font-semibold">{appointmentTime.weekday}</Typography>
                  <Divider orientation="vertical"
                    className="!self-center !mx-[0.2rem] !border-[1px] !border-gray-3 !h-3"/>
                  <Typography variant="h4">
                    {getBookingMonth(
                      appointmentDetails.start_at.toString(),
                      appointmentDetails.location.timeZone,
                    )} {appointmentTime.day}
                  </Typography>
                </Box>
              </Box>

              <Box className="flex gap-4 w-full">
                <ClockIcon />
                <Box className="flex flex-col gap-1">
                  <Typography variant="h4">
                    <span className="font-semibold">
                      {getAppointmentTypeText(appointmentDetails.appointmentCreateType)}
                    </span>
                    at{" "}
                    <span className="font-semibold">{appointmentTime.time}</span>
                  </Typography>
                </Box>
              </Box>


              <Box className="flex gap-4 w-full">
                <ClinicLocationIcon width={24} height={24} color={"#262626"}/>
                <Box className="flex flex-col gap-1">
                  <Typography variant="h4"><span className="font-semibold">GoodVets {" "}
                    {appointmentDetails?.location.locationName}</span>
                  </Typography>
                  <Typography variant="subtitle1">{appointmentDetails?.location.address}
                  </Typography>

                </Box>
              </Box>

              <Box className="border-b-2 w-[90%] ml-[5%] border-gray-3"></Box>

              <Box className="flex w-full flex-col gap-3">
                <Typography variant="body1" className="font-semibold text-gray-1">Hospital Contact</Typography>
                <a href={`mailto:${appointmentDetails?.location.email}`}>
                  <Box className="flex gap-4 items-center">
                    <UserMailIcon width={24} height={24}/>
                    <Typography className="!cursor-pointer font-semibold" variant="body1">
                      {appointmentDetails?.location.email}
                    </Typography>
                  </Box>
                </a>
                <a className="font-semibold" href={getContactString(appointmentDetails?.location.phoneNumber)}>
                  <Box className="flex gap-4 items-center">
                    <CallHospitalIcon width={24} height={24}/>
                    <Typography variant="body1">
                      {appointmentDetails?.location.phoneNumber}
                    </Typography>
                  </Box>
                </a>
              </Box>

              {appointmentDetails.promoCode && appointmentDetails.promoCode?.length > 0 && (
                <Box className="flex w-full flex-col gap-3">
                  <Typography variant="body1" className="font-semibold text-gray-1">Promo Applied</Typography>
                  {
                    appointmentDetails.promoCode?.map((promo)=>(
                      <PromoCodeUsed key={promo} promoCodeUsed={promo}/>))
                  }
                </Box>
              )}

            </Box>
          </ReasonForVisitContainerStyled>
        </Box>
        <Box className="flex flex-col items-center !sticky bottom-2 gap-1">
          {appointmentDetails?.status!=="Past" ?
            (() => {
              const isSingleUrgent = appointmentDetails?.appointmentCreateType === ASSIGNED_BOOKING_TYPE.URGENT_BOOKING;
              const canEditSingle = appointmentDetails?.allowedToEdit || (isSingleUrgent && singleRescheduleAllowed);
              return <>
                {canEditSingle &&
                <NewSecondaryButton
                  startIcon={<NewCalendarIcon />}
                  className="text-light-black !mb-0.5"
                  onClick={handleReScheduleVisit}>
                  Reschedule
                </NewSecondaryButton>}
                {canEditSingle &&
                <NewSecondaryButton
                  className="text-light-black !mb-0.5"
                  startIcon={<CalendarXIcon />}
                  onClick={handleCancelVisit}>
                  Cancel
                </NewSecondaryButton>}
                <GvSecondaryButton
                  type="submit"
                  className="!italic !font-semibold hover:!underline"
                  onClick={handleOpenAddToCalendarModal}>
                   Add to Calendar
                </GvSecondaryButton>
                {!canEditSingle &&
                <div className="italic font-semibold text-sm text-center my-2 w-80">
                  Please contact your hospital to reschedule or cancel this appointment
                </div>}
              </>;
            })() :
            null}
        </Box>
      </Box>}
    </>
  );
};

export default AppointmentReviewContainer;
